/**
 * Owner-view Overview tab body (tab 1 of 4) — PATTERN LEADER.
 *
 * A calm, read-only "how's my genie doing" snapshot rendered inside
 * GenieOwnerDetail. Establishes the SectionCard / Pill / Row rhythm the other
 * three owner tabs copy: a hero card (disc + name + greeting + read-only action
 * buttons + status badges) then three gradient-bordered SectionCards — Identity
 * & Purpose, Insights, and Knowledge Base.
 *
 * Read-only viewer: NO editable inputs. The one and only mutating affordance in
 * this flow is the "Tell our genie" concierge door, which lives in the shared
 * shell top bar — this body never mutates.
 *
 * Data: REAL `useAgent(id)` for identity/insights (and the hero brand logo via
 * resolveGenieAvatarUrl), REAL `useGenieKnowledge(id)` for the read-only
 * knowledge sources (KnowledgeViewResult). Brand law: genie tokens only; signature
 * gradient for hero accents; wireframe purple -> genie-secondary, green ->
 * genie-success; "Help Genie" two words; *Voice* italic; no "AI" language.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText,
  Lock,
  type LucideIcon,
  MessageSquare,
  Share2,
  Shield,
  Sparkles,
} from "lucide-react";
import GenieShareDrawer from "@/components/agent/drawers/GenieShareDrawer";
import { GenieDisc, Pill, Row, SectionCard } from "@/components/voice-genie/atoms";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";
import { mascotForAgent } from "@/components/voice-genie/GenieOwnerDetail";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { useAgent } from "@/hooks/agent/useAgents";
import { useGenieKnowledge } from "@/hooks/voice-vision";
import { getPurposeMeta } from "@/types/genie-purpose";
import type { Agent } from "@/types/agent/agent";
import type { LeadInfoField } from "@/types/leads/lead-info";
import type { KnowledgeSource } from "@/types/voice-vision";

const BLUE = "#4E9CFF";
const PINK = "#FF4DB8";

/* ────────────────────────── hero action button ──────────────────────── */

interface OverviewActionBtnProps {
  icon: LucideIcon;
  label: string;
  dark?: boolean;
  onClick?: () => void;
}

const OverviewActionBtn = ({ icon: Icon, label, dark, onClick }: OverviewActionBtnProps) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-[42px] px-4 rounded-xl flex items-center gap-2.5 transition-all ${
      dark
        ? "bg-[#1F2937] text-white shadow-lg shadow-[#1F2937]/30 hover:opacity-90"
        : "bg-white text-[#222B45] border border-genie-border shadow-sm hover:border-genie-primary/40"
    }`}
  >
    <Icon className={`w-4 h-4 ${dark ? "text-white" : "text-gray-500"}`} />
    <span className="text-[13.5px] font-bold">{label}</span>
  </button>
);

/* ─────────────────────────────── hero ───────────────────────────────── */

const OverviewHero = ({ agent }: { agent: Agent }) => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const [shareOpen, setShareOpen] = useState(false);
  const mascot = mascotForAgent(agent);
  const purpose = getPurposeMeta(agent.purpose);
  const isLive = agent.is_active;
  const brandLogoUrl = resolveGenieAvatarUrl(agent);
  const brandTint = agent.branding?.primaryColor as string | undefined;
  const greeting =
    agent.welcome_message ??
    agent.page?.welcome_message ??
    `Kia ora! Ask ${agent.name} anything.`;

  return (
    <div className="relative rounded-3xl border border-genie-border bg-white shadow-sm px-6 md:px-7 py-7 flex flex-col sm:flex-row items-center gap-6 sm:gap-7">
      {/* avatar — real brand logo when present, mascot disc as fallback */}
      <div className="flex-shrink-0">
        {brandLogoUrl ? (
          <div
            className="w-[150px] h-[150px] rounded-full flex items-center justify-center overflow-hidden"
            style={{
              backgroundColor: brandTint ? `${brandTint}1a` : "#F3F4F6",
            }}
          >
            <img
              src={brandLogoUrl}
              alt=""
              style={{
                width: 130,
                height: 130,
                borderRadius: "50%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </div>
        ) : (
          <VoiceMascot mascot={mascot} size={130} rounded discSize={150} />
        )}
      </div>

      {/* identity + read-only actions */}
      <div className="flex-1 min-w-0 flex flex-col gap-1 text-center sm:text-left">
        <h1 className="text-[32px] md:text-[36px] font-extrabold text-[#1A2238] tracking-[-1px] leading-tight">
          {agent.name}
        </h1>
        <p className="text-base text-gray-500 line-clamp-2">{greeting}</p>
        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2.5 mt-4">
          <OverviewActionBtn
            icon={MessageSquare}
            label="Quick Test"
            dark
            onClick={() => navigate(`/genies/${id}/immersive-setup`)}
          />
          <OverviewActionBtn
            icon={Share2}
            label="Share"
            onClick={() => setShareOpen(true)}
          />
          <OverviewActionBtn icon={Lock} label="Genie Access" />
          <OverviewActionBtn icon={Shield} label="Genie Trust Center" />
        </div>
      </div>

      {/* top-right badges */}
      <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
        <span className="inline-flex items-center gap-1.5 h-7 pl-3 pr-3 rounded-full bg-genie-secondary/10 text-genie-secondary">
          <Sparkles className="w-3 h-3" />
          <span className="text-[12.5px] font-bold">{purpose.label}</span>
        </span>
        <Pill tone="success" solid={false} className="gap-1.5">
          <span className="w-[7px] h-[7px] rounded-full bg-genie-success" />
          {isLive ? "Active" : "Paused"}
        </Pill>
      </div>

      <GenieShareDrawer genie={agent} isOpen={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
};

/* ─────────────────────── identity & purpose card ────────────────────── */

const OverviewIdentityCard = ({ agent }: { agent: Agent }) => {
  const purpose = getPurposeMeta(agent.purpose);
  const description =
    agent.description?.trim() ||
    `Customer-facing genie for ${agent.name}. Answers questions, helps callers, and captures follow-ups.`;

  return (
    <SectionCard
      genieIcon="genie-lamp"
      tint={PINK}
      title="Identity & Purpose"
      subtitle="Basic information and profile"
      className="mt-[18px]"
    >
      <Row label="Name" first>
        <span className="text-sm font-bold text-[#222B45]">{agent.name}</span>
      </Row>
      <Row label="Category">
        <Pill tone="neutral">{agent.category || "Other"}</Pill>
      </Row>
      <Row label="Purpose">
        <Pill tone="secondary">{purpose.label}</Pill>
      </Row>
      <Row label="Status">
        <Pill tone={agent.is_active ? "brand" : "neutral"} solid>
          {agent.is_active ? "Active" : "Paused"}
        </Pill>
      </Row>
      <Row label="Visibility">
        <Pill tone="brand" solid>
          {agent.is_public ? "Public" : "Private"}
        </Pill>
      </Row>
      <Row label="Marketplace">
        <Pill tone="secondary" solid>
          {agent.is_marketplace_genie ? "Yes" : "No"}
        </Pill>
      </Row>
      <div className="pt-3 border-t border-[#F3F4F6] flex flex-col gap-1.5">
        <span className="text-sm text-gray-500">Description</span>
        <p className="text-sm text-[#222B45] leading-[1.55]">{description}</p>
      </div>
    </SectionCard>
  );
};

/* ───────────────────────────── insights card ────────────────────────── */

const OverviewInsightsCard = ({ agent }: { agent: Agent }) => {
  const preset = agent.insights ?? agent.goal ?? null;
  const fields: LeadInfoField[] = preset?.fields ?? [];

  return (
    <SectionCard
      genieIcon="target"
      tint={BLUE}
      title="Insights"
      subtitle="What this genie listens for in every conversation"
      className="mt-[18px]"
    >
      {preset ? (
        <>
          {/* attached insight */}
          <div className="rounded-xl bg-[#F9FAFC] border border-[#F3F4F6] px-4 py-3.5">
            <p className="text-sm font-bold text-[#222B45]">{preset.name}</p>
            {preset.description && (
              <p className="text-[12.5px] text-gray-500 mt-1 leading-[1.45]">
                {preset.description}
              </p>
            )}
          </div>

          {/* fields configured (read-only) */}
          <div className="mt-3.5 rounded-xl border border-genie-border px-4 pt-3.5 pb-4">
            <p className="text-[13.5px] font-bold text-[#222B45]">
              {fields.length} {fields.length === 1 ? "field" : "fields"} captured
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              These details are pulled from each conversation automatically
            </p>
            {fields.length > 0 && (
              <div className="mt-3.5 flex flex-wrap gap-2.5">
                {fields.map((f) => (
                  <div
                    key={f.key}
                    className="flex items-center min-w-[200px] flex-grow basis-[22%] h-10 px-3.5 rounded-[9px] bg-[#F9FAFC] border border-genie-border"
                  >
                    <span className="text-[13px] text-[#222B45] truncate">{f.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center text-center py-7 px-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
            style={{ backgroundColor: `${BLUE}1a` }}
          >
            <Sparkles className="w-6 h-6" style={{ color: BLUE }} />
          </div>
          <p className="text-sm font-bold text-[#222B45]">No insights set up yet</p>
          <p className="text-[13px] text-gray-500 mt-1 max-w-[360px]">
            Tell our genie what to listen for, and we will pull it from every conversation for you.
          </p>
        </div>
      )}
    </SectionCard>
  );
};

/* ─────────────────────────── knowledge card ─────────────────────────── */

const OverviewKnowledgeRow = ({ source, first }: { source: KnowledgeSource; first: boolean }) => (
  <div
    className={`flex items-center gap-3.5 py-3.5 ${first ? "" : "border-t border-[#F3F4F6]"}`}
  >
    <div
      className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${BLUE}1a` }}
    >
      <FileText className="w-[18px] h-[18px]" style={{ color: BLUE }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-[#222B45] truncate">{source.name}</p>
      <p className="text-xs text-gray-500 mt-0.5 truncate">{source.meta}</p>
    </div>
  </div>
);

const OverviewKnowledgeCard = ({ genieId }: { genieId: string }) => {
  const { data, isLoading, isError } = useGenieKnowledge(genieId);
  const sources = data?.sources ?? [];

  return (
    <SectionCard
      genieIcon="book-open"
      tint={PINK}
      title="Knowledge Base"
      subtitle={data?.coverageNote ?? "What this genie knows from"}
      className="mt-[18px]"
    >
      {isLoading ? (
        <div className="flex flex-col">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`flex items-center gap-3.5 py-3.5 ${i === 0 ? "" : "border-t border-[#F3F4F6]"}`}>
              <div className="w-9 h-9 rounded-[10px] bg-gray-100 animate-pulse flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="h-3.5 w-1/2 rounded bg-gray-100 animate-pulse" />
                <div className="h-2.5 w-1/3 rounded bg-gray-100 animate-pulse mt-2" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center text-center py-7 px-4">
          <p className="text-sm font-bold text-[#222B45]">Couldn't load knowledge sources</p>
          <p className="text-[13px] text-gray-500 mt-1">Please try again in a moment.</p>
        </div>
      ) : sources.length === 0 ? (
        <div className="flex flex-col items-center text-center py-7 px-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
            style={{ backgroundColor: `${PINK}1a` }}
          >
            <FileText className="w-6 h-6" style={{ color: PINK }} />
          </div>
          <p className="text-sm font-bold text-[#222B45]">No knowledge added yet</p>
          <p className="text-[13px] text-gray-500 mt-1 max-w-[360px]">
            Tell our genie about your business and we will give it the documents and notes it needs.
          </p>
        </div>
      ) : (
        <div className="flex flex-col">
          {sources.map((s, i) => (
            <OverviewKnowledgeRow key={s.id} source={s} first={i === 0} />
          ))}
        </div>
      )}
    </SectionCard>
  );
};

/* ─────────────────────── loading + error states ─────────────────────── */

const OverviewSkeleton = () => (
  <div className="w-full">
    {/* hero skeleton */}
    <div className="rounded-3xl border border-genie-border bg-white shadow-sm px-6 md:px-7 py-7 flex flex-col sm:flex-row items-center gap-7">
      <div className="w-[150px] h-[150px] rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0 w-full flex flex-col gap-3">
        <div className="h-9 w-2/3 rounded-lg bg-gray-100 animate-pulse" />
        <div className="h-4 w-1/2 rounded bg-gray-100 animate-pulse" />
        <div className="flex gap-2.5 mt-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[42px] w-28 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      </div>
    </div>
    {/* three section-card skeletons */}
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="mt-[18px] p-[1.5px] rounded-[18px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm"
      >
        <div className="rounded-[16.5px] bg-white">
          <div className="flex items-center gap-3 px-5 md:px-6 py-4 border-b border-[#F3F4F6]">
            <div className="w-10 h-10 rounded-[11px] bg-gray-100 animate-pulse" />
            <div className="flex-1">
              <div className="h-4 w-40 rounded bg-gray-100 animate-pulse" />
              <div className="h-2.5 w-56 rounded bg-gray-100 animate-pulse mt-2" />
            </div>
          </div>
          <div className="px-5 md:px-6 pt-4 pb-5 flex flex-col gap-3">
            {[0, 1, 2, 3].map((r) => (
              <div key={r} className="h-4 w-full rounded bg-gray-100 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ──────────────────────────────── tab ───────────────────────────────── */

const OverviewTab = () => {
  const { id = "" } = useParams();
  const { data: agent, isLoading, isError } = useAgent(id);

  if (isLoading) return <OverviewSkeleton />;

  if (isError || !agent) {
    return (
      <div className="flex flex-col items-center text-center py-20">
        <div className="mb-4">
          <GenieDisc size={64} />
        </div>
        <h2 className="text-lg font-bold text-[#222B45]">We couldn't find this genie</h2>
        <p className="text-sm text-gray-500 mt-1 max-w-[380px]">
          It may have been moved. Head back to your gallery and pick a genie to view.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <OverviewHero agent={agent} />
      <OverviewIdentityCard agent={agent} />
      <OverviewInsightsCard agent={agent} />
      <OverviewKnowledgeCard genieId={id} />
    </div>
  );
};

export default OverviewTab;
